import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import styles from "../../styles/AccountModals.module.css";
import { BASE_URL } from "../../utils/constants";
import Spinner from "../../components/spinner";
import { handleApiError } from "../../utils/handleApiError";
import AlertDialog from "../../components/alert-dialogue";
import Button from "../../components/button-v1";

export const AddLocationStaffModal = ({
  isOpen,
  onClose,
  location_id,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    account_position: 1,
    primary_phone: "",
    active: true,
    account_level: "Admin",
    account_role: 4,
  });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");

    const fetchRoles = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/roles/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchPositions = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/positions/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchAllData = async () => {
      try {
        setModalLoading(true);

        const [rolesData, positionsData] = await Promise.all([
          fetchRoles(),
          fetchPositions(),
        ]);

        setRoles(rolesData);
        setPositions(positionsData);
      } catch (error) {
        handleApiError(error);
      } finally {
        setModalLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const addStaff = async (formData) => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/${location_id}/staff-onboard/`;

    try {
      setLoading(true);
      const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLoading(false);
      comp_renderer(!current_render);
      onClose();
      toast.success("Staff Onboarded successfully!");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const validateForm = () => {
    let errors = [];

    if (!formData.first_name.trim()) errors.push("First Name is required.");
    if (!formData.email.trim()) errors.push("Email is required.");
    if (!formData.password.trim()) {
      errors.push("Password is required.");
    }
    // else if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(formData.password)) {
    //     errors.push("Password must be at least 8 characters, alpha-numeric.");
    // }

    if (errors.length > 0) {
      toast.error(errors.join("\n"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line",
          textAlign: "left",
        },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {

    if (validateForm()) {
      addStaff(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.comp_header}>
          <p className={styles.comp_heading}>Create New Staff</p>
        </div>

        {modalLoading ? (
          <Spinner />
        ) : (
          <form
            className={`${styles.comp_wrapper} ${
              loading ? styles.loading : ""
            }`}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Phone</label>
                <input
                  className="no-arrows"
                  type="number"
                  name="primary_phone"
                  placeholder="Phone"
                  value={formData.primary_phone}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>Position</label>
                <select
                  name="account_position"
                  value={formData.account_position}
                  onChange={handleChange}
                >
                  {positions &&
                    positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={`${styles.input_group} ${styles.postal_code}`}>
              <div className={styles.input_field}>
                <label>Role</label>
                <select
                  name="account_role"
                  value={formData.account_role}
                  onChange={handleChange}
                >
                  <option value={4}>None</option>
                  <option value={1}>Account Admin</option>
                </select>
              </div>
            </div>
          </form>
        )}

        <div className={styles.submit_btn}>
          <button onClick={handleSubmit}>
            {loading ? "Creating..." : "Create"}
          </button>{" "}
          {loading ? <Spinner scale={5} /> : ""}
        </div>
      </div>
    </div>
  );
};

export const EditLocationStaffModal = ({
  isOpen,
  onClose,
  staff_info,
  location_id,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    first_name: staff_info.user.first_name,
    last_name: staff_info.user.last_name,
    email: staff_info.user.email,
    account_position: staff_info.account_position.id,
    account_role:
      staff_info.account_role === null ? "" : staff_info.account_role.id,
    primary_phone: staff_info.user.phone_number,
  });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");

    const fetchRoles = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/roles/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchPositions = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/positions/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchAllData = async () => {
      try {
        setModalLoading(true);

        const [rolesData, positionsData] = await Promise.all([
          fetchRoles(),
          fetchPositions(),
        ]);

        setRoles(rolesData);
        setPositions(positionsData);
      } catch (error) {
        handleApiError(error);
      } finally {
        setModalLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const EditStaff = async (FormData) => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/staff/${staff_info.id}/edit/`;

    try {
      setLoading(true);
      const response = await axios.patch(url, FormData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLoading(false);
      comp_renderer(!current_render);
      onClose();
      toast.success("Staff Edited successfully.");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const validateForm = (finalFormData) => {
    let errors = [];

    if (!finalFormData.first_name.trim())
      errors.push("First Name is required.");
    if (!finalFormData.email.trim()) errors.push("Email is required.");

    if (errors.length > 0) {
      toast.error(errors.join("\n"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line",
          textAlign: "left",
        },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const finalFormData = {
      ...formData,
      ...(formData.account_role === "" && { locations: [Number(location_id)] }),
    };

    if (validateForm(finalFormData)) {
      EditStaff(finalFormData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.comp_header}>
          <p className={styles.comp_heading}>Edit Staff</p>
        </div>

        {modalLoading ? (
          <Spinner />
        ) : (
          <form
            className={`${styles.comp_wrapper} ${
              loading ? styles.loading : ""
            }`}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>Phone</label>
                <input
                  className="no-arrows"
                  type="number"
                  name="primary_phone"
                  placeholder="Phone"
                  value={formData.primary_phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={`${styles.input_group} ${styles.postal_code}`}>
              <div className={styles.input_field}>
                <label>Position</label>
                <select
                  name="account_position"
                  value={formData.account_position}
                  onChange={handleChange}
                >
                  {positions &&
                    positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className={`${styles.input_group} ${styles.postal_code}`}>
              <div className={styles.input_field}>
                <label>Role</label>
                <select
                  name="account_role"
                  value={formData.account_role}
                  onChange={handleChange}
                >
                  <option value={4}>None</option>
                  <option value={1}>Account Admin</option>
                </select>
              </div>
            </div>
          </form>
        )}

        <div className={styles.submit_btn}>
          <button onClick={handleSubmit}>
            {loading ? "Updating..." : "Update"}
          </button>{" "}
          {loading ? <Spinner scale={5} /> : ""}
        </div>
      </div>
    </div>
  );
};

export const DeleteStaff = ({
  isOpen,
  onClose,
  staff_info,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const inactiveStaff = async () => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/staff/${staff_info.id}/edit/`;

    try {
      setLoading(true);
      const response = await axios.patch(
        url,
        { active: false },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      comp_renderer(!current_render);
      onClose();
      toast.success("Staff Deleted successfully.");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    inactiveStaff();
  };

  if (!isOpen) return null;

  return (
    <AlertDialog
      open={isOpen}
      setOpen={onClose}
      description={`Are you sure you want to delete ${staff_info.user.first_name} ${staff_info.user.last_name}`}
      title="Remove Staff"
    >
      <div className={`${styles.submit_btn} ${styles.submit_btn_2}`}>
        <Button variant="ghost" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        >
          {loading ? "Deleting..." : "Confirm"}
        </Button>
      </div>
    </AlertDialog>
  );
};
